import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { Fragment, useEffect, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { Separator } from '@/components/ui/separator';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { cn } from '@/utils/classnames';

import { calculateTotalBudget } from '../utils/channel-utils';
import { BudgetCalculationSection } from './budget-calculation-section';
import { OpeningBudget } from './opening-budget';

interface ManageOpeningsProps {
  channelKey: string;
  isFree?: boolean;
}

export function ManageOpenings({
  channelKey,
  isFree = false,
}: ManageOpeningsProps) {
  const { hookForm } = useJobPostingContext();

  const channel = useWatch({
    control: hookForm.control,
    name: `channels.${channelKey}`,
  });
  const { openingBudgets, budgetType, startsAt, endAt, budgetAllocation } =
    channel;

  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (budgetAllocation === 'per-funnel') {
      setIsExpanded(true);
    }
  }, [budgetAllocation]);

  const handleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div
      className={cn(
        'bg-fx-neutral-50 border-fx-neutral-200 flex flex-col gap-1 rounded-lg border p-0',
        isExpanded && 'pb-2',
      )}
    >
      <button
        type="button"
        className={cn(
          'hover:bg-fx-neutral-100 flex items-center justify-between rounded-lg p-4',
        )}
        onClick={handleExpand}
      >
        <Typography variant="body2" className="text-fx-neutral-600">
          <FormattedMessage
            defaultMessage="Manage Openings"
            description="Title of the manage openings section"
          />
        </Typography>

        {isExpanded ? (
          <ChevronUpIcon className="size-4 text-fx-neutral-600" />
        ) : (
          <ChevronDownIcon className="size-4 text-fx-neutral-600" />
        )}
      </button>

      {isExpanded && (
        <>
          <Separator />
          {Object.keys(openingBudgets).map((openingId, index) => (
            <Fragment key={openingId}>
              <OpeningBudget
                key={openingId}
                channelKey={channelKey}
                openingId={openingId}
                showDeleteButton={Object.keys(openingBudgets).length > 1}
                isFree={isFree}
              />
              {!(budgetType === 'lifetime' || !budgetType) && (
                <BudgetCalculationSection
                  channelKey={channelKey}
                  unitBudget={openingBudgets[openingId]}
                  totalBudget={calculateTotalBudget({
                    budgetType,
                    budgetPerUnit: openingBudgets[openingId],
                    startDate: startsAt,
                    endDate: endAt,
                  })}
                  className="p-4"
                  isOpeningSection
                />
              )}
              {index < Object.keys(openingBudgets).length - 1 && <Separator />}
            </Fragment>
          ))}
        </>
      )}
    </div>
  );
}
